import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { vesselsKeys } from '../vessels/queries';
import { uploadFileWithProgress, vesselStorageApi } from './service';
import type { VesselDocumentKind } from './types';

/**
 * React Query keys.
 *
 *   vesselStorageKeys.photoUrl(vesselId)        GET /photo/url
 *   vesselStorageKeys.documents(vesselId, kind?) GET /documents
 *
 * Mutations invalidate the relevant key + the parent vessel detail
 * (so `Vessel.photoKey` refreshes after upload/remove).
 */
export const vesselStorageKeys = {
  all: ['vessel-storage'] as const,
  photoUrl: (vesselId: string) =>
    [...vesselStorageKeys.all, 'photo-url', vesselId] as const,
  documents: (vesselId: string, kind?: VesselDocumentKind) =>
    [
      ...vesselStorageKeys.all,
      'documents',
      vesselId,
      kind ?? null,
    ] as const,
};

const VESSEL_ASSETS_CACHE = 'vessel-assets';

/** Drop the in-browser PWA cache entry for a presigned URL so a
 *  freshly-replaced photo isn't served stale. We strip the query
 *  string because cacheKeyWillBeUsed in the SW does the same. */
async function dropPwaCacheKey(url: string): Promise<void> {
  if (typeof caches === 'undefined') return;
  try {
    const cache = await caches.open(VESSEL_ASSETS_CACHE);
    await cache.delete(url.split('?')[0]);
  } catch {
    // No SW running (dev without PWA, tests) — safe to ignore.
  }
}

export function useVesselPhotoUrl(vesselId: string | undefined) {
  return useQuery({
    queryKey: vesselId
      ? vesselStorageKeys.photoUrl(vesselId)
      : vesselStorageKeys.all,
    queryFn: () => vesselStorageApi.getPhotoUrl(vesselId as string),
    enabled: Boolean(vesselId),
    // The URL is presigned for 15 min; refetch every 10 min so
    // long-lived dashboards don't suddenly hit 403.
    staleTime: 10 * 60 * 1000,
  });
}

export function useVesselDocuments(
  vesselId: string | undefined,
  kind?: VesselDocumentKind,
) {
  return useQuery({
    queryKey: vesselId
      ? vesselStorageKeys.documents(vesselId, kind)
      : vesselStorageKeys.all,
    queryFn: () =>
      vesselStorageApi.listDocuments(vesselId as string, { kind }),
    enabled: Boolean(vesselId),
  });
}

/**
 * Photo upload mutation — orchestrates the full 3-step flow:
 *   1. presign-upload (POST /api/.../presign-upload)
 *   2. PUT directly to MinIO (XHR, with optional progress callback)
 *   3. confirm-upload (POST /api/.../confirm-upload)
 *
 * The progress callback fires on step 2 only (the only step where a
 * sizeable payload moves over the wire).
 */
export function useUploadVesselPhoto(vesselId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      file: File;
      onProgress?: (loaded: number, total: number) => void;
      signal?: AbortSignal;
    }) => {
      const presigned = await vesselStorageApi.presignPhotoUpload(vesselId, {
        contentType: input.file.type,
        contentLength: input.file.size,
        fileName: input.file.name,
      });
      await uploadFileWithProgress({
        url: presigned.url,
        file: input.file,
        contentType: input.file.type,
        onProgress: input.onProgress,
        signal: input.signal,
      });
      return vesselStorageApi.confirmPhotoUpload(vesselId, {
        fileKey: presigned.fileKey,
        contentType: input.file.type,
        sizeBytes: input.file.size,
      });
    },
    onSuccess: async () => {
      // Drop the previous PWA cache entry so a stale photo isn't
      // served before the SW revalidates. We don't know the old URL
      // anymore (queryClient already wiped it) — fetch the current
      // cache and clear the whole `vessel-assets` namespace; modest
      // cost given how rarely operators hit this.
      if (typeof caches !== 'undefined') {
        try {
          await caches.delete(VESSEL_ASSETS_CACHE);
        } catch {
          // ignore
        }
      }
      qc.invalidateQueries({ queryKey: vesselStorageKeys.photoUrl(vesselId) });
      qc.invalidateQueries({ queryKey: vesselsKeys.detail(vesselId) });
    },
  });
}

export function useDeleteVesselPhoto(vesselId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => vesselStorageApi.deletePhoto(vesselId),
    onSuccess: async () => {
      const url = qc.getQueryData<{ url: string } | null>(
        vesselStorageKeys.photoUrl(vesselId),
      );
      if (url?.url) await dropPwaCacheKey(url.url);
      qc.invalidateQueries({ queryKey: vesselStorageKeys.photoUrl(vesselId) });
      qc.invalidateQueries({ queryKey: vesselsKeys.detail(vesselId) });
    },
  });
}

/**
 * Document upload mutation — same 3-step flow, with a `kind`
 * carried through to the final POST /documents.
 */
export function useUploadVesselDocument(vesselId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      file: File;
      kind: VesselDocumentKind;
      onProgress?: (loaded: number, total: number) => void;
      signal?: AbortSignal;
    }) => {
      const presigned = await vesselStorageApi.presignDocumentUpload(
        vesselId,
        {
          contentType: input.file.type,
          contentLength: input.file.size,
          fileName: input.file.name,
        },
      );
      await uploadFileWithProgress({
        url: presigned.url,
        file: input.file,
        contentType: input.file.type,
        onProgress: input.onProgress,
        signal: input.signal,
      });
      return vesselStorageApi.createDocument(vesselId, {
        kind: input.kind,
        fileKey: presigned.fileKey,
        fileName: input.file.name,
        contentType: input.file.type,
        sizeBytes: input.file.size,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...vesselStorageKeys.all, 'documents', vesselId],
      });
    },
  });
}

export function useDeleteVesselDocument(vesselId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (docId: string) =>
      vesselStorageApi.deleteDocument(vesselId, docId),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...vesselStorageKeys.all, 'documents', vesselId],
      });
    },
  });
}
